import { RefreshCw } from 'lucide-react';
import { Reveal } from '../Reveal';

const DeliveryDiagram = ({ stages, accent, diagramLabel, testidPrefix }) => {
  const positions = [
    'left-1/2 top-0 -translate-x-1/2',
    'right-0 top-1/2 -translate-y-1/2',
    'left-1/2 bottom-0 -translate-x-1/2',
    'left-0 top-1/2 -translate-y-1/2',
  ];
  const markerId = `arrowhead-${accent.replace('#', '')}`;
  return (
    <div
      role="img"
      aria-label={diagramLabel}
      data-testid={`${testidPrefix}-delivery-diagram`}
      className="relative mx-auto aspect-square w-full max-w-md"
    >
      <svg viewBox="0 0 400 400" className="absolute inset-0 h-full w-full" aria-hidden="true">
        <defs>
          <marker id={markerId} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <path d="M0,0 L8,4 L0,8 Z" fill={accent} />
          </marker>
        </defs>
        <circle cx="200" cy="200" r="132" fill="none" stroke="#C3CFE2" strokeWidth="1.5" strokeDasharray="5 7" />
        <line x1="298" y1="82" x2="318" y2="66" stroke={accent} strokeWidth="2" markerEnd={`url(#${markerId})`} />
      </svg>
      <div className="absolute left-1/2 top-1/2 flex h-28 w-28 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-navy-900 text-center shadow-lift">
        <RefreshCw className="h-5 w-5" style={{ color: accent }} aria-hidden="true" />
        <span className="mt-1.5 px-3 text-[10px] font-bold uppercase leading-tight tracking-[0.12em] text-white">
          Continuous improvement
        </span>
      </div>
      {stages.map((stage, i) => (
        <div
          key={stage.number}
          className={`absolute ${positions[i]} w-32 rounded-xl border border-slate-200 bg-white p-3 text-center shadow-card sm:w-36`}
        >
          <span className="font-mono text-[10px] font-medium tracking-widest" style={{ color: accent }}>{stage.number}</span>
          <span className="mt-0.5 block text-xs font-bold leading-tight text-navy-900 sm:text-sm">
            {stage.title}
          </span>
        </div>
      ))}
    </div>
  );
};

export const ServiceDelivery = ({ data, accent, index, testidPrefix, sectionClass = 'bg-mist' }) => (
  <section aria-labelledby={`${testidPrefix}-delivery-heading`} data-testid={`${testidPrefix}-delivery-section`} className={`${sectionClass} py-20 sm:py-28`}>
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest" style={{ color: accent }}>{index}</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{data.eyebrow}</span>
          </p>
          <h2 id={`${testidPrefix}-delivery-heading`} className="mt-4 text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            {data.heading}
          </h2>

          <ol className="relative mt-10 space-y-8">
            <span className="absolute bottom-6 left-[7px] top-2 w-px bg-slate-300" aria-hidden="true" />
            {data.stages.map((stage) => (
              <li key={stage.number} className="relative flex gap-5 pl-8" data-testid={`${testidPrefix}-delivery-stage-${stage.number}`}>
                <span className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 bg-white" style={{ borderColor: accent }} aria-hidden="true" />
                <div>
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-xs font-medium tracking-widest" style={{ color: accent }}>{stage.number}</span>
                    <h3 className="text-lg font-extrabold tracking-tight text-navy-900">{stage.title}</h3>
                  </div>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">{stage.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </Reveal>

        <Reveal delay={0.12}>
          <DeliveryDiagram
            stages={data.stages}
            accent={accent}
            diagramLabel={data.diagramLabel}
            testidPrefix={testidPrefix}
          />
        </Reveal>
      </div>
    </div>
  </section>
);
